/* eslint-disable prettier/prettier */
import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { IoTime } from "react-icons/io5";

import styled, { keyframes } from "styled-components";
import { lighten } from "polished";

import CircularProgressbar from "react-circular-progressbar";

import ProjectService from "../../services/projectService";
import { customToast as toast } from "../../utils";

export const getProjectClassName = (percentage) => {
  if (percentage < 70) {
    return "success";
  } if (percentage >= 70 && percentage < 100) {
    return "warning";
  }
  return "danger";
};
const ProjectProgressBar = ({
  project,
  type,
  showDetails,
  onClick,
  height,
  auth,
  totalBillable,
  total,
  uniqueId,
}) => {
  // const target = useRef(null);
  // const [show, setShow] = useState(false);
  const [data, setData] = useState({
    totalHours: total,
    totalBillableHours: totalBillable,
    fetched:
      typeof totalBillable === "number" || typeof total === "number" || false,
  });

  const [loading, setLoading] = useState(false);

  const fetchProjectBudget = useCallback(async () => {
    try {
      setLoading(true);
      if (type === "icon") {
        const fetchData = async () => {
          const result = await ProjectService.getBudgetConsumed(project._id);
          const amount = result && result[0] ? result[0].totalHours : 0;
          if (amount) {
            setData({ ...result[0], fetched: true });
          }
        };

        if (project?.consumedBudget && project.consumedBudget[0]) {
          setData({ ...project.consumedBudget[0], fetched: true });
        } else if (project?._id && !data.fetched) {
          fetchData();
        }
      } else {
        const result = await ProjectService.getBudgetConsumed(project._id);
        const amount = result && result[0] ? result[0].totalHours : 0;

        if (amount) {
          await setData({ ...result[0], fetched: true });
        } else {
          await setData({ ...data, fetched: true });
        }
      }
    } catch (err) {
      setLoading(false);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [data, project, setLoading, type]);

  useEffect(() => {
    if (
      !data.fetched &&
      !loading &&
      project &&
      typeof totalBillable !== "number" &&
      typeof total !== "number"
    ) {
      setLoading(true);
      setData({ ...data, fetched: true });
      fetchProjectBudget();
    }
  }, [
    fetchProjectBudget,
    type,
    data,
    loading,
    project,
    setLoading,
    total,
    totalBillable,
  ]);

  const actualHours = data?.totalHours || 0;

  const budgetHours =
    project && project.budget_hours ? project.budget_hours : 0;

  let percentage = 0;
  if (actualHours && budgetHours) {
    percentage = parseFloat(actualHours / budgetHours) * 100;
  }

  const projectClassName = getProjectClassName(percentage);

  const billingRate = project && project.rate ? project.rate : 0;

  const budgetConsumed =
    billingRate && actualHours ? billingRate * actualHours : 0;

  const budgetAmount =
    project && project.budget_amount ? project.budget_amount : 1;

  const actualHoursBalance = budgetHours - actualHours || 0;

  const getIconClassName = () => {
    if (data.fetched) {
      if (project?.budget_hours) {
        return projectClassName;
      }
      return "grey";
    }
    return "gravoc";
  };

  let display;
  switch (type) { 
    case "icon":
      display =
        data && data.fetched && project ? (
          <React.Fragment>
            <a
              id={uniqueId}
              data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
              href={`/app/projects/${project._id}`}
              rel="noopener noreferrer"
              target="_blank"
            >
              <IoTime className={`projectIcon ${getIconClassName()}`} />
            </a>
            <ReactTooltip
              anchorId={uniqueId}
              className={`custom-tooltip ${
                auth.isNightMode ? "dark" : "light"
              }`}
              place="left"
            >
              <p style={{ marginBottom: 5 }}>
                Budget Hours:&nbsp;<b>{parseFloat(budgetHours).toFixed(2)}</b>
              </p>
            <p style={{ marginBottom: 5 }}>
              Billable Hours:&nbsp;
              <b>{parseFloat(data?.totalBillableHours).toFixed(2)}</b>
            </p>
              <p style={{ marginBottom: 5 }}>
              Actual Hours:&nbsp;<b>{parseFloat(actualHours).toFixed(2)}</b>
              </p>
              <p style={{ marginBottom: 5 }}>
                Balance Hours:&nbsp;
              <b>{parseFloat(actualHoursBalance).toFixed(2)}</b>
              </p>
              <p>
                Rate:&nbsp;<b>${project.rate || 0}</b>
              </p>
            </ReactTooltip>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <IoTime
              id={uniqueId}
              className={`projectIcon ${getIconClassName()}`}
              data-tooltip-content="Loading..."
              data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
            />
            <ReactTooltip
              anchorId={uniqueId}
              className={`custom-tooltip ${
                auth.isNightMode ? "dark" : "light"
              }`}
              place="left"
            />
          </React.Fragment>
        );
      break;
    case "circular":
      if (project?.budget_hours) {
        display = (
          <CircularProgressbar
            percentage={percentage}
            strokeWidth={10}
            className={`progressbar-${projectClassName}`}
            text={`${parseInt(percentage).toLocaleString()}%`}
          />
        );
      } else {
        display = (
          <h6
            style={{
              textAlign: "center",
              fontSize: 15,
              color: auth.isNightMode ? "#FFF" : "#262626",
            }}
          >
            {actualHours || 0} hrs
          </h6>
        );
      }

      break;
    default:
      display = (
        <StyledBar
          height={height}
          className={`project-progress-container ${showDetails &&
            "flex -center"}`}
          style={{ padding: "0 5px" }}
          onClick={onClick}
        >
          <div className="project-progress-bar">
            <div
              className={`progress ${
                project?.budget_hours ? projectClassName : "bg-gravoc"
              }`}
              style={{
                width: `${project?.budget_hours ? percentage : "100"}%`,
              }}
            />
          </div>
          {showDetails && auth && (
            <>
              <p
                id="info"
                data-tooltip-content={`$${budgetConsumed.toLocaleString(
                  undefined,
                  {
                    maximumFractionDigits: 2,
                  }
                )} / $${budgetAmount.toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                })}`}
                data-tooltip-variant={auth.isNightMode ? "dark" : "light"}
                data-html
                data-for="info"
              >
                {actualHours} / {budgetHours} <small>HRS</small>
              </p>
              <ReactTooltip
                anchorId="info"
                className={`custom-tooltip ${
                  auth.isNightMode ? "dark" : "light"
                }`}
                place="left"
                type={auth.isNightMode ? "dark" : "light"}
                effect="solid"
                globalEventOff="click"
              />
            </>
          )}
        </StyledBar>
      );
      break;
  }

  return <div className="project-progress-bar">{display}</div>;
};

const fadeIn = keyframes`
    from { 
        opacity: 0;
    }
    to {
        opacity: 1
    }
`;

const StyledBar = styled.div`
  width: 100%;
  .project-progress-bar {
    width: 100%;
    animation: ${fadeIn} 2000ms cubic-bezier(0.5, 0, 0.5, 1);
    background: ${(props) => props.theme.light_grey};
    height: ${(props) => props.height}px;
    overflow: hidden;
    .progress {
      height: 100%;
    }
  }
  p {
    white-space: nowrap;
    margin: 0;
    margin-left: 15px;
    font-size: 12px;
  }
  .progress {
    height: 100%;
    max-width: 100%;
    border: none !important;
    transition: width 2s ease-in-out;
    cursor: pointer;
    &.success {
      background: ${(props) => props.theme.green_g};
      &:hover {
        background: ${(props) => lighten(0.03, props.theme.green_g)};
      }
      p {
        color: #fff !important;
      }
    }
    &.warning {
      background: ${(props) => props.theme.gold_g};
      &:hover {
        background: ${(props) => lighten(0.03, props.theme.gold_g)};
      }
    }
    &.danger {
      background: ${(props) => props.theme.red_g};
      &:hover {
        background: ${(props) => lighten(0.03, props.theme.red_g)};
      }
      p {
        color: #fff !important;
      }
    }
  }
  @media screen and (max-width: 767px) {
    padding: 10px 15px 0 15px !important;
    .project-progress {
      margin: 15px 0 !important;
      padding: 0 !important;
    }
  }
`;

ProjectProgressBar.propTypes = {
  project: PropTypes.object,
  type: PropTypes.string,
  showDetails: PropTypes.bool,
  onClick: PropTypes.func,
  height: PropTypes.number,
  auth: PropTypes.object,
  totalBillable: PropTypes.number,
  total: PropTypes.number,
  uniqueId: PropTypes.string,
};

ProjectProgressBar.defaultProps = {
  type: "",
  height: 8,
  totalBillable: null,
  total: null,
  uniqueId: null,
};
export default ProjectProgressBar;
